<template>
    <Teleport v-if="loginContainer && initialized" :to="loginContainer">
        <div v-if="userInfo" class="user-info">
            <a :href="accountOverviewLink" class="btn-flex-container btn-flex-container--secondary"
                aria-label="Access my account">
                <div class="btn"><i class="fa fa-user" aria-hidden="true"></i>My Design Tool Account</div>
            </a>
        </div>
        <div v-else>
            <button @click="signIn" class="btn-flex-container btn-flex-container--secondary"
                aria-label="Login or create an account">
                <div class="btn"><i class="fa fa-user" aria-hidden="true"></i>Login or Create Account</div>
            </button>
        </div>
    </Teleport>

    <Teleport v-if="frameContainer" :to="frameContainer">

        <!--KBMax frame container-->
        <div v-show="displayState == 'frame'" ref="kbMaxDiv" :id="uniqueId" :class="appContainerClass" style="min-height:75vh"></div>

        <!--Saving report-->
        <div v-if="displayState == 'saving'" class="mw-section mw-margin u-txt-center">
            <h3 class="h h-md-thin">
                {{ messageHeader }}
            </h3>
            <img :src="'/Content/images/Loading_Animation.gif'" />
        </div>

        <!--Error -->
        <div v-if="displayState == 'error'" class="mw-section mw-margin u-txt-center">
            <h3 class="h h-md-thin">
                {{ messageHeader }}
            </h3>
            <p v-if="messageBody">
                {{ messageBody }}
            </p>
            <div class="u-mrg-center max-width-md u-flex u-flex--justify-center u-flex--gap-base u-flex--wrap">
                <a :href="accountOverviewLink" class="btn-flex-container btn-flex-container--secondary" aria-label="Return to your projects">
                    <div class="btn">Return to your projects</div>
                </a>
            </div>
        </div>

        <!--Thank you-->
        <div v-if="displayState == 'thank-you'" class="mw-section mw-margin u-txt-center">
            <div v-html="thankYouHtml" class="designer-html"></div>
            <div v-if="thankYouCta" class="u-mrg-center max-width-md u-flex u-flex--justify-center u-flex--gap-base u-flex--wrap">
                <a :href="thankYouCta.href" :target="thankYouCta.target" :title="thankYouCta.tooltip"
                    class="btn-flex-container btn-flex-container--secondary">
                    <div class="btn">{{thankYouCta.text}}</div>
                </a>
            </div>
        </div>

        <!--Optional save button-->
        <div v-if="showSaveButton && displayState == 'frame'" class="u-padd-top-half u-txt-center u-mrg-center">
            <button name="submit" id="save-quote" class="btn btn--primary" @click="saveProject"
                aria-label="Save project">
                {{ isUnsubmitted ? "Save Project" : "Clone Project" }}
            </button>
        </div>

        <!--Locked-->
        <div v-if="displayState == 'locked'" class="mw-section mw-margin u-txt-center">
            <h3 class="h h-md-thin">
                Your project is being processed.
            </h3>
            <p>Projects in progress cannot be edited.</p>
            <p>Currently the project is in step: {{ props.quoteStatus.state }}</p>
            <div class="u-mrg-center max-width-md u-flex u-flex--justify-center u-flex--gap-base u-flex--wrap">
                <a :href="accountOverviewLink" class="btn-flex-container btn-flex-container--secondary" aria-label="Return to your projects">
                    <div class="btn">Return to your projects</div>
                </a>
                <a :href="currentUrl" class="btn-flex-container btn-flex-container--secondary" aria-label="Try again">
                    <div class="btn">Try again</div>
                </a>
            </div>
        </div>
    </Teleport>

    <Teleport to="#portal-target-2">
        <modals-container></modals-container>
    </Teleport>
</template>

<script setup>

import { ref, onMounted, onUnmounted, computed, nextTick } from 'vue'
import { ModalsContainer } from 'vue-final-modal'
import modalManager from '../modules/modalManager'
import kbmax from '../modules/kbmax/embed'
import simpleUserApi from '../utils/api/simpleUserApi'
import ga from '../utils/ga'

// Generate a unique ID for this component instance
const uniqueId = `kbmax-${Math.random().toString(36).substring(2, 9)}`;

//api configuration (still brought in from the APIDEF window variables, this should be refactored when the services are consolidated to a single service)
//see: KbMaxEmbedController and api configs in Global.asax.cs
//these are set on mount
let submitEndpoint = "";
let saveEndpoint = "";

//where to store the configurator instance
let embed = null;
let ignoreFirstPageChange = true;

const props = defineProps({
    signInDialogModel: {
        default: () => ({}),
        type: Object
    },
    configuredProduct: {
        default: () => ({}),
        type: Object
    },
    quoteStatus: {
        default: () => ({
            state: "",
            canOpen: false
        }),
        type: Object

    },
    isUnsubmitted: {
        type: Boolean,
        default: true
    },
    loginContainer: {
        type: String
    },
    frameContainer: {
        type: String
    },
    kbMaxUrl: {
        type: String
    },
    quoteId: {
        type: String
    },
    configuratorId: {
        type: String
    },
    toolName: {
        type: String,
        default: "Unknown tool"
    },
    accountOverviewLink: {
        type: String,
        default: "/account"
    },
    thankYouHtml: {
        type: String,
        default: ""
    },
    thankYouCta: {
        type: Object
    },
    showSaveButton: {
        type: Boolean,
        default: false
    },
    campaignId: {
        type: String,
        default: ""
    },
    pardotUrl: {
        type: String,
        default: ""
    }
});

const initialized = ref(false);
const appContainerClass = ref("normal");
const kbMaxDiv = ref(null);
const userInfo = ref(null);
const messageHeader = ref("");
const messageBody = ref("");

const displayState = ref("frame");

const lastMessage = ref("");

const currentUrl = computed(() => window.location.href);

function signIn() {
    modalManager.showSignIn(props.signInDialogModel);
}

async function ga_sendEvent(eventName) {

    if (eventName == 'submit') {

        const flds = await embed.getFieldsAsync();
        const toRep = flds["Field - Send to ADS Sales Representative"];
        const optIn = flds["fOptInMarketing"];

        const extraFields = {
            'sendto_salesrep': toRep,
            'optin_marketing': optIn,
            "tool_name": props.toolName,
            "user_status": userInfo.value ? "loggedin" : "notloggedin"
        }

        if (toRep) {
            ga.push(userInfo.value ? 'toolproject-submitaskrep-loggedin' : 'toolproject-submitaskrep-notloggedin',
                `${props.toolName}_tool`,
                extraFields);
        } else {
            ga.push(userInfo.value ? 'toolproject-submit-loggedin' : 'toolproject-submit-notloggedin',
                `${props.toolName}_tool`,
                extraFields);
        }

        if (optIn) {
            ga.push('news-optin', `${props.toolName}_tool`);
        }

    } else if (eventName == 'start') {
        ga.pushOnce(userInfo.value ? 'toolproject-start-loggedin' : 'toolproject-start-notloggedin', `${props.toolName}_tool`);
    } else if (eventName == 'full-screen') {
        ga.push('toolproject-full-screen', `${props.toolName}_tool`);
    }


}

async function handleMessage(data, msg) {
    lastMessage.value = data;

    console.info("KbMaxRevised.vue received frame message: ", data);

    if (data.includes("UserWantsToLogIn")) {
        signIn();
    } else if (data.includes("UserClickedSave")) {
        await saveProject();
    } else if (data.includes("UserClickedSubmit")) {
        await submitProject();
    } else if (data.includes("UserChangedPage")) {
        //ignore the first page change for tracking purposes (the initial page)
        if (ignoreFirstPageChange) {
            ignoreFirstPageChange = false;
            return;
        }
        ga_sendEvent("start");
    } else if (data.includes("UserFullScreen")) {
        ga_sendEvent("full-screen");
    } else if (data.includes("UserUnFullScreen")) {
        //place holder in case we want actions for leaving full screen
    }
}

async function submitProject() {
    window.scrollTo(0, 0);
    messageHeader.value = "Submitting your project...";
    displayState.value = "saving";

    await ga_sendEvent("submit");

    //ensure the scroll and message happens before we start submitting
    nextTick(async () => {
        const result = await embed.submitProject(submitEndpoint, props.quoteId, props.toolName, props.pardotUrl, props.campaignId);
        if (result.success) {
            displayState.value = "thank-you";
        } else {
            displayState.value = "error";
            messageHeader.value = "Unable to submit your project."
            messageBody.value = `Please try again later or contact support. ${result.responseText}`
        }
    });
}

async function saveProject() {

    if (!userInfo.value) {
        signIn();
        return;
    }

    window.scrollTo(0, 0);
    displayState.value = "saving";
    messageHeader.value = "Saving your project...";

    //ensure that the scroll and message happens before we start saving
    nextTick(async () => {
        const result = await embed.saveProject(saveEndpoint, props.quoteId, props.toolName, !props.isUnsubmitted)
        if (result.success) {
            window.location.href = props.accountOverviewLink;
        } else {
            displayState.value = "error";
            messageHeader.value = "Unable to save your project.";
            messageBody.value = `Please try again later or contact support. ${result.responseText}`
        }
    });
}

async function updateUserInfo() {
    const userResponse = await simpleUserApi.getUserInfo();
    if (userResponse?.success && userResponse.data) {
        userInfo.value = userResponse.data;
    } else {
        userInfo.value = null;
    }

    return userInfo.value;
}

async function setupConfiguratorAfterSignIn() {
    //update the user information
    await updateUserInfo();

    //set logged in fields
    embed.configurator.setFields({ "IsUserLoggedIn": userInfo.value ? "True" : "False" });
    if (userInfo.value) {
        embed.setDefaultUserFields(userInfo.value);
    }
}

onMounted(async () => {
    //try to update user info to see if we are already logged in
    await updateUserInfo();

    //try to set endpoints
    submitEndpoint = window.APIDEF?.kbmaxSubmitEndpoint;
    saveEndpoint = window.APIDEF?.kbmaxSaveEndpoint;

    if (!submitEndpoint || !saveEndpoint) {
        console.error("Missing KBMax endpoint(s) on window.APIDEF. Refusing to load embed.");
        return;
    }

    // Remove any frame placeholder divs from the frame container
    if (props.frameContainer) {
        const container = document.querySelector(props.frameContainer);
        if (container) {
            const placeholders = container.querySelectorAll('div.frame-placeholder');
            placeholders.forEach(placeholder => placeholder.remove());
        }
    }

    //render kbmax into the ref
    if (kbMaxDiv.value && props.quoteStatus.canOpen) {
        embed = kbmax.createConfigurator(
            props.kbMaxUrl,
            props.configuratorId,
            props.configuredProduct,
            uniqueId);

        //create a global for debugging and testing purposes
        window.kbMaxConfigurator = {embed, displayState, messageHeader, messageBody};

        //attach message handler
        embed.attachMessageHandler(handleMessage);

        //set logged in fields
        embed.configurator.setFields({ "IsUserLoggedIn": userInfo.value ? "True" : "False" });
        if (userInfo.value) {
            embed.setDefaultUserFields(userInfo.value);
        }

        //tell tool if this is unsubmitted
        embed.configurator.setFields({"fIsUnsubmitted": props.isUnsubmitted?"True":"False"});

        //attach log in event handler for the signed-in event which is emited by the SignInDialog through the modal manager
        window.addEventListener("signed-in", setupConfiguratorAfterSignIn);
    } else if (!props.quoteStatus.canOpen) {
        displayState.value = "locked";
    }

    initialized.value = true;
});

onUnmounted(() => {
    //destroy configurator
    if (embed) {
        embed.dispose();
    }

    //remove event handler
    window.removeEventListener("signed-in", setupConfiguratorAfterSignIn);
});


</script>

<style scoped>
.normal {
    height: 75vh;
}
</style>